import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LineChart } from "lucide-react";
import { RecrystaMark } from "@/components/RecrystaLogo";

export default function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) nav(loc.state?.from || "/dashboard");
    else setErr(res.error);
  };

  return (
    <div className="flex min-h-screen w-full">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-24 py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-2 mb-10">
            <RecrystaMark size={36} />
            <div className="text-lg font-bold tracking-tight text-slate-900">Recrysta</div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            Welcome back
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Sign in to your workspace to continue.
          </p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full rounded-md border-0 py-2 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1">Password</label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full rounded-md border-0 py-2 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                placeholder="At least 8 characters"
              />
            </div>
            {err && (
              <div className="text-sm text-red-600" data-testid="login-error">{err}</div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 transition-colors disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-4 text-right">
            <Link
              to="/forgot-password"
              data-testid="link-forgot-password"
              className="text-xs text-slate-500 hover:text-slate-900"
            >
              Forgot your password?
            </Link>
          </div>
          <div className="mt-6 text-sm text-slate-500">
            No account?{" "}
            <Link to="/signup" data-testid="link-signup" className="font-semibold text-blue-600 hover:text-blue-700">
              Create one
            </Link>
          </div>
        </div>
      </div>
      <div className="hidden lg:flex w-1/2 bg-slate-950 p-12 flex-col justify-between text-white">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Pipeline Intelligence</div>
          <h2 className="mt-6 text-4xl font-extrabold tracking-tighter leading-[1.05]">
            See what your <br /> HubSpot pipeline <br /> is actually telling you.
          </h2>
          <p className="mt-6 text-slate-400 max-w-md">
            Historical snapshots. Health scoring. Clean stage mapping. Built for small B2B teams that
            deserve better than a black-box CRM.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6 mt-12">
          {[
            { k: "Snapshots", v: "Daily, append-only" },
            { k: "Isolation", v: "Strict per account" },
            { k: "Hosting", v: "EU region" },
          ].map((it) => (
            <div key={it.k} className="border-t border-slate-800 pt-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{it.k}</div>
              <div className="mt-1 text-sm font-semibold text-white">{it.v}</div>
            </div>
          ))}
        </div>
        <div className="text-xs text-slate-600 flex items-center gap-2">
          <LineChart className="h-4 w-4" />
          Trusted by revenue leaders who prefer clarity.
        </div>
      </div>
    </div>
  );
}
